/* eslint-disable react-hooks/exhaustive-deps */
// DocumentViewer.tsx

import React, { useState, useEffect, useRef } from "react";
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonFooter,
    IonButtons,
    IonButton,
    useIonLoading,
    useIonAlert,
    useIonToast,
    IonText,
    IonItem,
    IonLabel,
    IonTextarea,
    IonIcon,
    IonCard,
    IonCardContent,
} from "@ionic/react";
import {
    fetchPendingDocumentById,
    fetchDocumentByAccessHash,
    signDocument,
    declineDocument,
} from "../services/DocumentServices";
import { getApiUrl } from "../helpers/DevHelper";
import { useAuth } from "../context/Auth";
import { Document, Page, pdfjs } from "react-pdf";
import { useHistory } from "react-router-dom";
import { checkmarkCircleOutline, closeCircleOutline } from "ionicons/icons";

import "./DocumentViewer.scss";

// Set the workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface DocumentViewerProps {
    documentId?: string | null;
    eSignHash?: string | null;
    isOpen: boolean;
    onClose: () => void;
    onDocumentAction?: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    documentId,
    eSignHash,
    isOpen,
    onClose,
    onDocumentAction,
}) => {
    const [presentLoading, dismissLoading] = useIonLoading();
    const [presentAlert] = useIonAlert();
    const [presentToast] = useIonToast();
    const [documentData, setDocumentData] = useState<any>(null);
    const { checkAxiosError } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [pdfFile, setPdfFile] = useState<Blob | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const contentRef = useRef<HTMLIonContentElement | null>(null);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState<boolean>(false);
    const history = useHistory();

    // State for decline reason modal
    const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
    const [declineReason, setDeclineReason] = useState<string>("");
    const [declineError, setDeclineError] = useState<string>("");

    useEffect(() => {
        if (isOpen && (documentId || eSignHash)) {
            setIsScrolledToEnd(false); // Reset the scroll state when modal opens
            presentLoading("Betöltés...");
            if (documentId) {
                fetchPendingDocumentById(documentId)
                    .then((res: any) => {
                        if (res.data.error === false) {
                            setDocumentData(res.data.data);
                        } else {
                            presentAlert({
                                header: "Hiba",
                                message: "A dokumentum betöltése nem sikerült.",
                                buttons: ["OK"],
                            });
                            onClose();
                        }
                    })
                    .catch((err: any) => {
                        checkAxiosError(err);
                        presentAlert({
                            header: "Hiba",
                            message: "A dokumentum betöltése nem sikerült.",
                            buttons: ["OK"],
                        });
                        onClose();
                    })
                    .finally(() => {
                        dismissLoading();
                    });
            } else if (eSignHash) {
                fetchDocumentByAccessHash(eSignHash)
                    .then((res: any) => {
                        if (res.data.error === false) {
                            setDocumentData(res.data.data);
                        } else {
                            presentAlert({
                                header: "Hiba",
                                message: "A dokumentum betöltése nem sikerült.",
                                buttons: ["OK"],
                            });
                            onClose();
                        }
                    })
                    .catch((err: any) => {
                        presentAlert({
                            header: "Hiba",
                            message: "A dokumentum betöltése nem sikerült.",
                            buttons: ["OK"],
                        });
                        onClose();
                    })
                    .finally(() => {
                        dismissLoading();
                    });
            }
        } else {
            setDocumentData(null);
        }
    }, [isOpen, documentId, eSignHash]);

    const getDocumentUrl = () => {
        if (
            documentData &&
            documentData.file &&
            documentData.file.access_hash
        ) {
            return `${getApiUrl()}/v1/files/file/${
                documentData.file.access_hash
            }`;
        }
        return null;
    };

    useEffect(() => {
        const fetchPdf = async () => {
            const url = getDocumentUrl();
            if (url) {
                try {
                    const response = await fetch(url);
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }
                    const blob = await response.blob();
                    if (blob.size === 0) {
                        throw new Error("Received empty PDF file.");
                    }
                    setPdfFile(blob);
                } catch (error: any) {
                    console.error("Error fetching PDF:", error);
                    setError(
                        `A PDF fájl letöltése nem sikerült: ${error.message}`
                    );
                }
            }
        };

        if (documentData) {
            fetchPdf();
        }
    }, [documentData]);

    useEffect(() => {
        if (pdfFile) {
            const url = URL.createObjectURL(pdfFile);
            setBlobUrl(url);

            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [pdfFile]);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleScroll = async () => {
        if (contentRef.current) {
            const scrollElement = await contentRef.current.getScrollElement();
            const scrollTop = scrollElement.scrollTop;
            const scrollHeight = scrollElement.scrollHeight;
            const offsetHeight = scrollElement.offsetHeight;

            if (scrollTop + offsetHeight >= scrollHeight - 10) {
                // User has scrolled to the bottom (with a tolerance of 10 pixels)
                setIsScrolledToEnd(true);
            }
        }
    };

    const handleSignDocument = () => {
        presentLoading("Feldolgozás...");
        signDocument(documentData.e_sign_hash)
            .then((res: any) => {
                if (res.data.error === false) {
                    setDocumentData(res.data.data); // Update documentData with new status
                    presentToast({
                        message: "A dokumentum sikeresen aláírva.",
                        duration: 2000,
                        color: "success",
                    });

                    // Call the onDocumentAction callback if provided
                    if (onDocumentAction) {
                        onDocumentAction();
                    }

                    // If eSignHash is null, present the alert
                    if (!eSignHash) {
                        presentAlert({
                            header: "Elfogadás sikeres",
                            message: "A dokumentum elfogadása megtörtént",
                            buttons: [
                                {
                                    text: "Rendben",
                                    handler: () => {
                                        onClose();
                                        history.push("/my/dashboard");
                                    },
                                },
                            ],
                        });
                    }
                } else {
                    presentAlert({
                        header: "Hiba",
                        message:
                            "A dokumentum aláírása technikai okból jelenleg nem lehetséges. Kérjük próbálja újra később.",
                        buttons: ["OK"],
                    });
                }
            })
            .catch((err: any) => {
                presentAlert({
                    header: "Hiba",
                    message:
                        "A dokumentum aláírása technikai okból jelenleg nem lehetséges. Kérjük próbálja újra később.",
                    buttons: ["OK"],
                });
            })
            .finally(() => {
                dismissLoading();
            });
    };

    const handleDeclineDocument = () => {
        setDeclineReason("");
        setDeclineError("");
        setShowDeclineModal(true);
    };

    const submitDecline = () => {
        const reason = declineReason.trim();
        if (!reason || reason.length < 1 || reason.length > 256) {
            setDeclineError(
                "Az elutasítás indoka legalább 1 és legfeljebb 256 karakter lehet."
            );
            return;
        }
        setDeclineError("");
        setShowDeclineModal(false);
        presentLoading("Feldolgozás...");
        declineDocument(documentData.e_sign_hash, reason)
            .then((res: any) => {
                if (res.data.error === false) {
                    setDocumentData(res.data.data); // Update documentData with new status
                    presentToast({
                        message: "A dokumentum sikeresen elutasítva.",
                        duration: 2000,
                        color: "success",
                    });

                    // Call the onDocumentAction callback if provided
                    if (onDocumentAction) {
                        onDocumentAction();
                    }

                    // If eSignHash is null, present the alert
                    if (!eSignHash) {
                        presentAlert({
                            header: "Elutasítás sikeres",
                            message: "A dokumentum elutasítása megtörtént",
                            buttons: [
                                {
                                    text: "Rendben",
                                    handler: () => {
                                        onClose();
                                        history.push("/my/dashboard");
                                    },
                                },
                            ],
                        });
                    }
                } else {
                    presentAlert({
                        header: "Hiba",
                        message:
                            "A dokumentum elutasítása technikai okból jelenleg nem lehetséges. Kérjük próbálja újra később.",
                        buttons: ["OK"],
                    });
                }
            })
            .catch((err: any) => {
                presentAlert({
                    header: "Hiba",
                    message:
                        "A dokumentum elutasítása technikai okból jelenleg nem lehetséges. Kérjük próbálja újra később.",
                    buttons: ["OK"],
                });
            })
            .finally(() => {
                dismissLoading();
            });
    };

    const handleDownload = () => {
        if (pdfFile && blobUrl) {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = documentData?.title
                ? `${documentData.title}.pdf`
                : "dokumentum.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const renderFooterContent = () => {
        if (documentData?.e_sign_status === "SIGNED") {
            return (
                <IonText color="success">
                    <p style={{ textAlign: "center", margin: "1rem" }}>
                        A dokumentum már elfogadásra került.
                    </p>
                </IonText>
            );
        } else if (documentData?.e_sign_status === "DECLINED") {
            return (
                <IonText color="danger">
                    <p style={{ textAlign: "center", margin: "1rem" }}>
                        A dokumentum elutasítása megtörtént. Kérjük várjon, míg
                        munkatárunk felveszi Önnel a kapcsolatot.
                    </p>
                </IonText>
            );
        } else {
            return (
                <div className="button-container">
                    <IonButton
                        disabled={!isScrolledToEnd}
                        fill="solid"
                        color="danger"
                        onClick={handleDeclineDocument}
                    >
                        Elutasítás
                    </IonButton>
                    <IonButton
                        disabled={!isScrolledToEnd}
                        fill="solid"
                        color="success"
                        onClick={handleSignDocument}
                    >
                        Elfogadás
                    </IonButton>
                </div>
            );
        }
    };

    const calculatePageWidth = () => {
        const padding = 32; // Total horizontal padding (16px on each side)
        const maxPageWidth = 1200; // Maximum width for the PDF content

        // Calculate available width
        const availableWidth = window.innerWidth - padding;

        // Return the lesser of the available width and the maximum page width
        return Math.min(availableWidth, maxPageWidth);
    };

    const shouldHidePdfContent =
        eSignHash &&
        (documentData?.e_sign_status === "DECLINED" ||
            documentData?.e_sign_status === "SIGNED");

    return (
        <>
            <IonModal
                isOpen={isOpen}
                onDidDismiss={onClose}
                cssClass="document-viewer-modal"
            >
                <IonHeader>
                    <IonToolbar className="multiline-toolbar">
                        <div className="custom-title">
                            {documentData?.title ?? "Dokumentum"}
                        </div>
                        {!eSignHash && (
                            <IonButtons slot="end">
                                <IonButton onClick={onClose}>Bezárás</IonButton>
                            </IonButtons>
                        )}
                    </IonToolbar>
                </IonHeader>
                <IonContent
                    ref={contentRef}
                    onIonScroll={handleScroll}
                    scrollEvents={true}
                >
                    {shouldHidePdfContent ? (
                        <div className="status-container">
                            {documentData?.e_sign_status === "SIGNED" && (
                                <IonCard className="status-card">
                                    <IonCardContent className="status-card-content">
                                        <IonIcon
                                            icon={checkmarkCircleOutline}
                                            className="status-icon success"
                                        />
                                        <IonText color="success">
                                            <h2 className="status-message">
                                                A dokumentum elfogadásra került.
                                            </h2>
                                        </IonText>
                                        {pdfFile && (
                                            <IonButton
                                                onClick={handleDownload}
                                                expand="block"
                                                style={{ marginTop: "1rem" }}
                                            >
                                                Dokumentum letöltése
                                            </IonButton>
                                        )}
                                    </IonCardContent>
                                </IonCard>
                            )}
                            {documentData?.e_sign_status === "DECLINED" && (
                                <IonCard className="status-card">
                                    <IonCardContent className="status-card-content">
                                        <IonIcon
                                            icon={closeCircleOutline}
                                            className="status-icon danger"
                                        />
                                        <IonText color="danger">
                                            <h2 className="status-message">
                                                A dokumentum elutasítása
                                                megtörtént. Kérjük várjon, míg
                                                munkatárunk felveszi Önnel a
                                                kapcsolatot.
                                            </h2>
                                        </IonText>
                                    </IonCardContent>
                                </IonCard>
                            )}
                        </div>
                    ) : error ? (
                        <div style={{ padding: "1rem", textAlign: "center" }}>
                            <p>{error}</p>
                            {!eSignHash && (
                                <IonButton onClick={onClose}>Bezárás</IonButton>
                            )}
                        </div>
                    ) : blobUrl ? (
                        <div className="pdf-container">
                            <Document
                                file={blobUrl}
                                onLoadSuccess={onDocumentLoadSuccess}
                                loading="Betöltés..."
                                error="A dokumentum betöltése nem sikerült."
                            >
                                {Array.from(
                                    new Array(numPages),
                                    (el, index) => (
                                        <div
                                            key={`page_wrapper_${index + 1}`}
                                            className="page-wrapper"
                                        >
                                            <Page
                                                key={`page_${index + 1}`}
                                                pageNumber={index + 1}
                                                width={calculatePageWidth()}
                                            />
                                        </div>
                                    )
                                )}
                            </Document>
                        </div>
                    ) : (
                        <div style={{ padding: "1rem", textAlign: "center" }}>
                            <p>Betöltés...</p>
                        </div>
                    )}
                </IonContent>
                {!shouldHidePdfContent && (
                    <IonFooter>
                        <IonToolbar>{renderFooterContent()}</IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            {/* Decline Reason Modal */}
            <IonModal
                isOpen={showDeclineModal}
                onDidDismiss={() => setShowDeclineModal(false)}
                cssClass="full-screen-modal"
            >
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Dokumentum elutasítása</IonTitle>
                        <IonButtons slot="end">
                            <IonButton
                                onClick={() => setShowDeclineModal(false)}
                            >
                                Mégse
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    <div style={{ padding: "1rem" }}>
                        <IonItem>
                            <IonLabel position="stacked">
                                Kérjük írja be az elutasítás okát (1-256
                                karakter)
                            </IonLabel>
                            <IonTextarea
                                value={declineReason}
                                onIonChange={(e) =>
                                    setDeclineReason(e.detail.value!)
                                }
                                maxlength={256}
                            />
                        </IonItem>
                        {declineError && (
                            <IonText color="danger">
                                <p>{declineError}</p>
                            </IonText>
                        )}
                    </div>
                </IonContent>
                <IonFooter>
                    <IonToolbar>
                        <IonButtons>
                            <IonButton
                                onClick={submitDecline}
                                color="danger"
                                fill="solid"
                                style={{ width: "100%" }}
                            >
                                Elutasítás
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonFooter>
            </IonModal>
        </>
    );
};

export default DocumentViewer;
